import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
    FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Alert, Typography
} from '@mui/material';
import { Gender, UserBase } from '../../types/user';
import { Formik, Form, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import useUserApi from '../../api/useUserApi';

interface EditUserProps {
    open: boolean;
    onClose: () => void;
    onSave: (userData: Partial<UserBase> & { password?: string }) => void;
    editingUser: UserBase | null;
}

interface FormValues extends UserBase {
    password: string;
    isEditing: boolean;
}

const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('שם פרטי הוא שדה חובה'),
    last_name: Yup.string().required('שם משפחה הוא שדה חובה'),
    email: Yup.string().email('כתובת אימייל לא תקינה').required('אימייל הוא שדה חובה'),
    mobile: Yup.string(),
    birth_date: Yup.string().required('תאריך לידה הוא שדה חובה'),
    birth_time: Yup.string(),
    country_of_birth: Yup.string(),
    city_of_birth: Yup.string(),
    gender: Yup.string().required('מגדר הוא שדה חובה'),
    confirm_marketing_content: Yup.boolean(),
    is_admin: Yup.boolean(),
    password: Yup.string().when('isEditing', {
        is: false,
        then: (schema) => schema.required('סיסמה היא שדה חובה'),
        otherwise: (schema) => schema,
    }),
});

const EditUser: React.FC<EditUserProps> = ({ open, onClose, onSave, editingUser }) => {
    const { adminChangePassword } = useUserApi();
    const [adminPassword, setAdminPassword] = useState('');
    const [adminPasswordLoading, setAdminPasswordLoading] = useState(false);
    const [adminPasswordError, setAdminPasswordError] = useState('');
    const [adminPasswordSuccess, setAdminPasswordSuccess] = useState('');
    const initialValues: FormValues = {
        id: editingUser?.id,
        first_name: editingUser?.first_name || '',
        last_name: editingUser?.last_name || '',
        email: editingUser?.email || '',
        mobile: editingUser?.mobile || '',
        birth_date: editingUser?.birth_date || '',
        birth_time: editingUser?.birth_time || '',
        country_of_birth: editingUser?.country_of_birth || '',
        city_of_birth: editingUser?.city_of_birth || '',
        gender: editingUser?.gender || Gender.MALE,
        confirm_marketing_content: editingUser?.confirm_marketing_content ?? true,
        is_admin: editingUser?.is_admin || false,
        password: '',
        isEditing: !!editingUser,
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{editingUser ? 'ערוך משתמש' : 'הוסף משתמש'}</DialogTitle>
            <Formik<FormValues>
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
                    const { isEditing, password, ...userData } = values;
                    onSave({
                        ...userData,
                        ...(editingUser ? {} : { password }),
                    });
                    setSubmitting(false);
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, setFieldValue }: FormikProps<FormValues>) => (
                    <Form>
                        <DialogContent>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                                <TextField
                                    name="first_name"
                                    label="שם פרטי"
                                    fullWidth
                                    value={values.first_name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.first_name && Boolean(errors.first_name)}
                                    helperText={touched.first_name && errors.first_name}
                                />
                                <TextField
                                    name="last_name"
                                    label="שם משפחה"
                                    fullWidth
                                    value={values.last_name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.last_name && Boolean(errors.last_name)}
                                    helperText={touched.last_name && errors.last_name}
                                />
                                <TextField
                                    name="email"
                                    label="אימייל"
                                    fullWidth
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                />
                                <TextField
                                    name="mobile"
                                    label="טלפון"
                                    fullWidth
                                    value={values.mobile}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <TextField
                                    name="birth_date"
                                    label="תאריך לידה"
                                    type="date"
                                    fullWidth
                                    value={values.birth_date}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.birth_date && Boolean(errors.birth_date)}
                                    helperText={touched.birth_date && errors.birth_date}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    name="birth_time"
                                    label="שעת לידה"
                                    type="time"
                                    fullWidth
                                    value={values.birth_time}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    name="country_of_birth"
                                    label="ארץ לידה"
                                    fullWidth
                                    value={values.country_of_birth}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <TextField
                                    name="city_of_birth"
                                    label="עיר לידה"
                                    fullWidth
                                    value={values.city_of_birth}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                                <FormControl fullWidth>
                                    <InputLabel>מגדר</InputLabel>
                                    <Select
                                        name="gender"
                                        value={values.gender}
                                        label="מגדר"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.gender && Boolean(errors.gender)}
                                    >
                                        <MenuItem value={Gender.MALE}>זכר</MenuItem>
                                        <MenuItem value={Gender.FEMALE}>נקבה</MenuItem>
                                    </Select>
                                    {touched.gender && errors.gender && (
                                        <div style={{ color: 'red', fontSize: '0.75rem', marginTop: '3px' }}>
                                            {errors.gender}
                                        </div>
                                    )}
                                </FormControl>                               
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="confirm_marketing_content"
                                            checked={values.confirm_marketing_content}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="אישור לקבלת תוכן שיווקי"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="is_admin"
                                            checked={values.is_admin}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="מנהל מערכת"
                                />
                                {!editingUser && (
                                    <TextField
                                        name="password"
                                        label="סיסמה"
                                        type="password"
                                        fullWidth
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.password && Boolean(errors.password)}
                                        helperText={touched.password && errors.password}
                                    />
                                )}
                            </Box>
                            {/* Admin password change section */}
                            {editingUser && (
                              <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle1" gutterBottom>שנה סיסמה עבור משתמש זה</Typography>
                                {adminPasswordError && <Alert severity="error" sx={{ mb: 1 }}>{adminPasswordError}</Alert>}
                                {adminPasswordSuccess && <Alert severity="success" sx={{ mb: 1 }}>{adminPasswordSuccess}</Alert>}
                                <TextField
                                  label="סיסמה חדשה (לפחות 8 תווים)"
                                  type="password"
                                  value={adminPassword}
                                  onChange={e => setAdminPassword(e.target.value)}
                                  fullWidth
                                  sx={{ mb: 1 }}
                                  inputProps={{ minLength: 8 }}
                                />
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  disabled={adminPasswordLoading || adminPassword.length < 8}
                                  onClick={async () => {
                                    setAdminPasswordError('');
                                    setAdminPasswordSuccess('');
                                    setAdminPasswordLoading(true);
                                    try {
                                      await adminChangePassword(editingUser.id!, { new_password: adminPassword });
                                      setAdminPasswordSuccess('הסיסמה שונתה בהצלחה!');
                                      setAdminPassword('');
                                    } catch (err) {
                                      setAdminPasswordError('שגיאה בשינוי הסיסמה.');
                                    } finally {
                                      setAdminPasswordLoading(false);
                                    }
                                  }}
                                >
                                  שנה סיסמה
                                </Button>
                              </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose}>ביטול</Button>
                            <Button type="submit">שמירה</Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default EditUser; 